import React, { useEffect, useState, useRef, useContext, useMemo } from "react";
import { useLocation } from "react-router-dom";
import Pagination from '../../components/pagination/pagination';
import SubuserStatementTable from "../../components/subuserStatementTable";
import SubUserStatementTableModal from "../../components/subuserStatementTableModal";
import ViewLayout from "../../components/viewLayout";
import { useFetch } from "../../utils/fetchHook";
import { getCookie } from "../../jwt/_helpers/cookie";
import TableHelper from "../../components/tableHelper";
import { Alert } from "reactstrap";
import { StateContext } from "../../utils/context";
import { sendAuditEvent } from "../../utils/auditTrail";
import TenantContext from "../../utils/tenantContext";

const SubuserStatements = () => {
  const location = useLocation();
  const parentReportFilterId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get("subuser_report__parent_report");
    return raw != null && raw !== "" ? raw : null;
  }, [location.search]);

  const parentReportApiSuffix =
    parentReportFilterId != null
      ? `&subuser_report__parent_report=${encodeURIComponent(parentReportFilterId)}`
      : "";

  const token = getCookie("token");
  const {currentUser} = useContext(StateContext);
  const {tenant} = useContext(TenantContext);

  const didEff1 = useRef(false);
  const didEff2 = useRef(false);
  const [forceUpdate, setForce] = useState(1);
  const [dataCount, setDataCount] = useState(0);
  const [currrentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [query, setQuery] = useState('');
  const [ordering, setOrdering] = useState({order: '-', name: 'created', sort: 'created'});
  const [subUser, setSubUser] = useState('');
  const [subUserEndpoint, setSubUserEndpoint] = useState('');
  const [subusersUserOnly, setSubusersOnly] = useState('&subusers_only=true');

  const [openModal, setToggleModal] = useState(false);
  const [dataModal, setDataModal] = useState(null);
  const [lastAction, setLastAction] = useState("");
  const [updateBody, setUpdateBody] = useState(false);

  //ajax handlers
  const [success, setSuccess] = useState(false);
  const [errorPut, setError] = useState(false);

  const handlerValues = { setSuccess, setError, forceUpdate, setForce };

  const [apiData, error, loading] = useFetch("GET", `subuser-statements/${subUserEndpoint}?ordering=${ordering.order + ordering.name}&page=${currrentPage}&page_size=${pageSize}&search=${query}${subusersUserOnly}${parentReportApiSuffix}`, token, false, forceUpdate);
  const [tableData, setTableData] = useState([]);
  const lastAuditSignature = useRef("");

  useEffect(() => {
    if(apiData?.detail === "Invalid page.") {
      setCurrentPage(1);
      return;
    }

    if(apiData) {
      if(apiData.results) {
        setTableData(apiData.results);
      }
    }
  }, [apiData]);

  useEffect(() => {
    if (apiData?.detail === "Invalid page.") {
      setCurrentPage(1);
      return;
    }

    if (apiData) {
      if(apiData.count) {
        setDataCount(apiData.count);
      }
      if(apiData.results) {
        setTableData(apiData.results);
      }
    }
  }, [apiData]);

  useEffect(() => {
    if (!didEff1.current) {
      didEff1.current = true;
      return;
    }
    setForce(prev => prev + 1);
  }, [currrentPage, pageSize, ordering.order, ordering.name, subUser, parentReportFilterId]);


  useEffect(() => {
    if (!didEff2.current) {
      didEff2.current = true;
      return;
    }
    const timeout = setTimeout(() => {
      setForce(prev => prev + 1);
    }, 500)
    return () => clearTimeout(timeout)
  }, [query])

  useEffect(() => {
    if (loading || !apiData || !Array.isArray(tableData)) return;

    const statementIds = tableData.map(statement => statement.id);
    const signature = JSON.stringify({
      route: window.location.pathname,
      page: currrentPage,
      pageSize,
      ordering: `${ordering.order}${ordering.name}`,
      query,
      subUserEndpoint,
      statementIds,
    });

    if (signature === lastAuditSignature.current) return;
    lastAuditSignature.current = signature;

    sendAuditEvent({
      token,
      eventType: 4,
      route: window.location.pathname,
      entityType: "subuser_statement",
      entityIds: statementIds,
      tenantContext: tenant,
      metadata: {
        page: currrentPage,
        page_size: pageSize,
        query,
        sub_user_endpoint: subUserEndpoint || null,
      },
    });
  }, [apiData, tableData, loading, token, currrentPage, pageSize, ordering.order, ordering.name, query, subUserEndpoint]);


  const paginationProps = {
    pageSize, setPageSize, dataCount, setCurrentPage, currrentPage
  }

  const defaultSorted = [{
    dataField: ordering.sort,
    order: ordering.order === "-" ? "desc" : "asc"
  }] 

  const tableProps = {
    tableData,
    defaultSorted,
  };

  return (
    <>
      {loading ? (
        <TableHelper loading />
      ) : error ? (
        <TableHelper error />
      ) : (
        <div>
        { 
          (!currentUser.statements) &&
          <Alert color="danger">
            You don't have access to this module, please contact the support team for further information.
          </Alert>
        }
        {
          (currentUser.statements) &&
          <>
            <ViewLayout title={"Your Subuser's Statement list"}>
              <SubuserStatementTable
                {...tableProps}
                parentReportId={parentReportFilterId}
                setSuccess={setSuccess}
                setError={setError}
                setToggleModal={setToggleModal}
                setDataModal={setDataModal}
                setLastAction={setLastAction}
                forceUpdate={forceUpdate}
                setForce={setForce}
                searchQuery={{setQuery, query, ordering, setOrdering}}
                subUserData={{subUser, setSubUser, subUserEndpoint, setSubUserEndpoint, setSubusersOnly}}
                auditData={{ token, route: "/subuser-statements", entityType: "subuser_statement" }}
              />
            </ViewLayout>
            <Pagination pagination={paginationProps}/>
          </>
        }
        </div>
      )}

      {dataModal && (
        <SubUserStatementTableModal
          openModal={openModal}
          setToggleModal={setToggleModal}
          values={handlerValues}
          modalKind={dataModal.modalKind === "visibility" ? "visibility" : "status"}
          title={
            dataModal.modalKind === "visibility"
              ? (dataModal.visibility === "active"
                  ? `Make statement invisible`
                  : `Make statement visible`)
              : ((dataModal.status === 0) 
                  ? `Mark statement as PAID`
                  : `Mark statement as OPEN`
                )
          }
          body={
            dataModal.modalKind === "visibility"
              ? (dataModal.visibility === "active"
                  ? `Are you sure you want to make the statement ${dataModal.name} invisible to subuser?`
                  : `Are you sure you want to make the statement ${dataModal.name} visible to subuser?`)
              : ((dataModal.status === 0)
                  ? `Are you sure you want to mark the statement ${dataModal.name} as PAID ?`
                  : `Are you sure you want to mark the statement ${dataModal.name} as OPEN ?`)
          }
          apiURL={
            dataModal.modalKind === "visibility"
              ? "subuser-statements/update-visibility"
              : "subuser-statements/update-status"
          }
          statementID={dataModal.id}
          statementStatus={dataModal.status}
          statementVisibility={dataModal.visibility}
        />
      )}
      {success && <Alert color="success">Subuser statement has been {lastAction}!</Alert>}
      {errorPut && (
        <Alert color="danger">
          Something went wrong! Please refresh page and try again!
        </Alert>
      )}
    </>
  );
};

export default SubuserStatements;
